/**
 * Bot roster definitions
 *
 * To add a new bot:
 * 1. Append a new BotDefinition to BOT_DEFINITIONS
 * 2. Run `npm run init-bots` to create/update Firebase accounts
 *
 * Time control restrictions:
 * - Sprint (1min+2s): Only Fast (2s) AIs — slower AIs timeout
 * - Standard (3min+4s): Fast (2s) + Standard (7s) AIs
 * - Marathon (10min+10s): All AIs including Deep (12s)
 */

import { AIModel, TimeFrame } from '../zodiacEngine/AI/Core/AIVariants';
import { PieceType } from '../zodiacEngine/types';
import { BotDefinition } from './types';

export const BOT_UID_PREFIX = 'bot_';

export const BOT_DEFINITIONS: BotDefinition[] = [
  // === Fast AIs (2s) — eligible for all time controls ===
  {
    uid: 'bot_flora',
    username: 'Flora',
    config: {
      aiModel: AIModel.GAIA,
      timeFrame: TimeFrame.FAST,
      startingElo: 650,
      allowedTimeControls: ['sprint', 'standard', 'marathon'],
      preferredKings: [PieceType.Bear, PieceType.Rhino, PieceType.Wolf],
    },
  },
  {
    uid: 'bot_drone',
    username: 'Drone',
    config: {
      aiModel: AIModel.SENTINEL,
      timeFrame: TimeFrame.FAST,
      startingElo: 700,
      allowedTimeControls: ['sprint', 'standard', 'marathon'],
      preferredKings: [PieceType.Bear, PieceType.Wolf, PieceType.Crane],
    },
  },
  {
    uid: 'bot_jester',
    username: 'Jester',
    config: {
      aiModel: AIModel.ALCHEMIST,
      timeFrame: TimeFrame.FAST,
      startingElo: 650,
      allowedTimeControls: ['sprint', 'standard', 'marathon'],
      preferredKings: [PieceType.Monkey, PieceType.Frog, PieceType.Bear],
    },
  },
  {
    uid: 'bot_nadir',
    username: 'Nadir',
    config: {
      aiModel: AIModel.ZENITH,
      timeFrame: TimeFrame.FAST,
      startingElo: 750,
      allowedTimeControls: ['sprint', 'standard', 'marathon'],
      preferredKings: [PieceType.Bear, PieceType.Rhino, PieceType.Snake],
      bannedPieces: [PieceType.Virus],
    },
  },
  {
    uid: 'bot_seeker',
    username: 'Seeker',
    config: {
      aiModel: AIModel.HUNTER,
      timeFrame: TimeFrame.FAST,
      startingElo: 700,
      allowedTimeControls: ['sprint', 'standard', 'marathon'],
      preferredKings: [PieceType.Bear, PieceType.Wolf, PieceType.Snake],
    },
  },
  {
    uid: 'bot_bullet',
    username: 'Bullet',
    config: {
      aiModel: AIModel.ARROW,
      timeFrame: TimeFrame.FAST,
      startingElo: 650,
      allowedTimeControls: ['sprint', 'standard', 'marathon'],
      preferredKings: [PieceType.Crane, PieceType.Rhino, PieceType.Penguin],
    },
  },
  {
    uid: 'bot_creature',
    username: 'Creature',
    config: {
      aiModel: AIModel.CREATURE,
      timeFrame: TimeFrame.FAST,
      startingElo: 700,
      allowedTimeControls: ['sprint', 'standard', 'marathon'],
      preferredKings: [PieceType.Bear, PieceType.Rhino, PieceType.Wolf],
    },
  },

  {
    uid: 'bot_ember',
    username: 'Ember',
    config: {
      aiModel: AIModel.WISP,
      timeFrame: TimeFrame.FAST,
      startingElo: 600,
      allowedTimeControls: ['sprint', 'standard', 'marathon'],
      preferredKings: [PieceType.Crane, PieceType.Penguin, PieceType.Snake],
    },
  },

  // === Standard AIs (7s) — eligible for standard + marathon ===
  {
    uid: 'bot_alchemist',
    username: 'Alchemist',
    config: {
      aiModel: AIModel.ALCHEMIST,
      timeFrame: TimeFrame.STANDARD,
      startingElo: 800,
      allowedTimeControls: ['standard', 'marathon'],
      preferredKings: [PieceType.Monkey, PieceType.Frog, PieceType.Bear],
    },
  },
  {
    uid: 'bot_prima',
    username: 'Prima',
    config: {
      aiModel: AIModel.GAIA,
      timeFrame: TimeFrame.STANDARD,
      startingElo: 825,
      allowedTimeControls: ['standard', 'marathon'],
      preferredKings: [PieceType.Bear, PieceType.Rhino, PieceType.Wolf],
    },
  },
  {
    uid: 'bot_arrow',
    username: 'Arrow',
    config: {
      aiModel: AIModel.ARROW,
      timeFrame: TimeFrame.STANDARD,
      startingElo: 850,
      allowedTimeControls: ['standard', 'marathon'],
      preferredKings: [PieceType.Crane, PieceType.Rhino, PieceType.Penguin],
    },
  },
  {
    uid: 'bot_sentinel',
    username: 'Sentinel',
    config: {
      aiModel: AIModel.SENTINEL,
      timeFrame: TimeFrame.STANDARD,
      startingElo: 875,
      allowedTimeControls: ['standard', 'marathon'],
      preferredKings: [PieceType.Bear, PieceType.Wolf, PieceType.Rhino],
    },
  },
  {
    uid: 'bot_golem',
    username: 'Golem',
    config: {
      aiModel: AIModel.CREATURE,
      timeFrame: TimeFrame.STANDARD,
      startingElo: 900,
      allowedTimeControls: ['standard', 'marathon'],
      preferredKings: [PieceType.Bear, PieceType.Rhino, PieceType.Wolf],
    },
  },
  {
    uid: 'bot_hunter',
    username: 'Hunter',
    config: {
      aiModel: AIModel.HUNTER,
      timeFrame: TimeFrame.STANDARD,
      startingElo: 925,
      allowedTimeControls: ['standard', 'marathon'],
      preferredKings: [PieceType.Bear, PieceType.Wolf, PieceType.Snake],
    },
  },
  {
    uid: 'bot_poe',
    username: 'Poe',
    config: {
      aiModel: AIModel.WISP,
      timeFrame: TimeFrame.STANDARD,
      startingElo: 850,
      allowedTimeControls: ['standard', 'marathon'],
      preferredKings: [PieceType.Crane, PieceType.Penguin, PieceType.Frog],
    },
  },
  {
    uid: 'bot_nexus',
    username: 'Nexus',
    config: {
      aiModel: AIModel.ZENITH,
      timeFrame: TimeFrame.STANDARD,
      startingElo: 950,
      allowedTimeControls: ['standard', 'marathon'],
      preferredKings: [PieceType.Bear, PieceType.Rhino, PieceType.Snake],
      bannedPieces: [PieceType.Virus],
    },
  },

  // === Deep AIs (12s) — marathon only ===
  {
    uid: 'bot_oracle',
    username: 'Oracle',
    config: {
      aiModel: AIModel.ALCHEMIST,
      timeFrame: TimeFrame.DEEP,
      startingElo: 950,
      allowedTimeControls: ['marathon'],
      preferredKings: [PieceType.Bear, PieceType.Monkey, PieceType.Frog],
    },
  },
  {
    uid: 'bot_zenith',
    username: 'Zenith',
    config: {
      aiModel: AIModel.ZENITH,
      timeFrame: TimeFrame.DEEP,
      startingElo: 1050,
      allowedTimeControls: ['marathon'],
      preferredKings: [PieceType.Bear, PieceType.Rhino, PieceType.Wolf],
      bannedPieces: [PieceType.Virus],
    },
  },
  {
    uid: 'bot_gaia',
    username: 'Gaia',
    config: {
      aiModel: AIModel.GAIA,
      timeFrame: TimeFrame.DEEP,
      startingElo: 1000,
      allowedTimeControls: ['marathon'],
      preferredKings: [PieceType.Bear, PieceType.Rhino, PieceType.Wolf],
    },
  },
  {
    uid: 'bot_mainframe',
    username: 'Mainframe',
    config: {
      aiModel: AIModel.SENTINEL,
      timeFrame: TimeFrame.DEEP,
      startingElo: 1025,
      allowedTimeControls: ['marathon'],
      preferredKings: [PieceType.Bear, PieceType.Wolf, PieceType.Rhino],
    },
  },
  {
    uid: 'bot_rocket',
    username: 'Rocket',
    config: {
      aiModel: AIModel.ARROW,
      timeFrame: TimeFrame.DEEP,
      startingElo: 1050,
      allowedTimeControls: ['marathon'],
      preferredKings: [PieceType.Crane, PieceType.Rhino, PieceType.Penguin],
    },
  },
  {
    uid: 'bot_stalker',
    username: 'Stalker',
    config: {
      aiModel: AIModel.HUNTER,
      timeFrame: TimeFrame.DEEP,
      startingElo: 1075,
      allowedTimeControls: ['marathon'],
      preferredKings: [PieceType.Bear, PieceType.Wolf, PieceType.Snake],
    },
  },
  {
    uid: 'bot_eidolon',
    username: 'Eidolon',
    config: {
      aiModel: AIModel.CREATURE,
      timeFrame: TimeFrame.DEEP,
      startingElo: 1100,
      allowedTimeControls: ['marathon'],
      preferredKings: [PieceType.Bear, PieceType.Rhino, PieceType.Wolf],
    },
  },
  {
    uid: 'bot_wisp',
    username: 'Wisp',
    config: {
      aiModel: AIModel.WISP,
      timeFrame: TimeFrame.DEEP,
      startingElo: 950,
      allowedTimeControls: ['marathon'],
      preferredKings: [PieceType.Crane, PieceType.Penguin, PieceType.Snake],
    },
  },
];
